/* ---------------------------------------------------------------------------
   The Bridge Inspection Summary, printed.

   `QC-07`. This is not a report of this application's design. It is DoR's own
   form — *Guidelines for Inspection and Maintenance of Bridges, Volume 1,
   Part 2*, Appendix C — reproduced from what the register holds:

     BRIDGE IDENTIFICATION      number, district, name, road link
     INSPECTION SUMMARY         date, type, inspected by, division
     the ten items              1 Approach road … 10 River training, each with
                                Condition, Extent, Urgency and Comments
     RECOMMENDATIONS FOR REMEDIAL WORK
     Signed …                   Date …

   WHY REPRODUCE THE FORM RATHER THAN DESIGN A BETTER ONE. Because the sheet has
   a job in a process: the Divisional Engineer uses it to set maintenance
   priorities for the division, those priorities feed the Annual Road Maintenance
   Plan, and the ARMP is compiled nationally before it goes to Roads Board Nepal.
   A better-looking document that does not match the form is a document somebody
   has to transcribe. It also gives this feature an acceptance test that is not a
   matter of taste: does the printed sheet match the form.

   THE GAPS ARE PRINTED AS GAPS. Every honest thing this project does comes down
   to the same rule and it applies hardest here, because a printed sheet outlives
   the screen it came from and gets read by people who cannot ask it questions.
   An item nobody assessed prints blank, exactly as it would on paper. An `N`
   prints as `N` with its two other boxes empty, which is what the guideline
   instructs. A recommendation nobody can price prints the reason where the money
   would go.

   ELEVEN ITEMS ON THE FORM, TEN ROWS ON THIS SHEET. Culverts has an inspection
   form and no summary row — a structure that IS a culvert is not one of the ten
   parts of a bridge — so `SUMMARY_ITEMS` is what this draws, and a culvert
   assessment appears under the recommendations rather than inventing row 11.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import type { State } from '../core/store.ts';
import { emptyState } from '../components/widgets.ts';
import { paper } from '../components/paper.ts';
import { findBridge, inspectionTypeLabel, prettyDate } from '../data/derive.ts';
import { SUMMARY_ITEMS } from '../data/elements.ts';
import {
  CONDITION_LABELS,
  EXTENT_DEFINITIONS,
  URGENCY_DEFINITIONS,
} from '../data/ratingGuide.ts';
import { estimateFor, npr, refusalText, totalCaveat, type Estimate } from '../data/estimate.ts';
import { describeQuantity, trimNumber } from '../data/quantities.ts';
import { isCostable, wordingMap } from '../data/remedial.ts';
import { isOpenStatus, statusOn } from '../data/defects.ts';
import type { Bridge, ElementCondition, Inspection, RegisterDefect } from '../data/types.ts';
import { join, shown } from '../data/recorded.ts';
import { vocab } from '../tenant/agency.ts';

/**
 * The inspection this sheet summarises: the most recent one that assessed any
 * element.
 *
 * NOT SIMPLY THE MOST RECENT. The sheet's ten rows are element rows, and an
 * inspection that recorded only an overall judgement would print ten blank rows
 * under a date — which reads as a structure where nothing was found rather than
 * as a visit that did not use this form. Falling back to the newest inspection
 * with a breakdown prints the most recent sheet that was actually filled in, and
 * the header carries that visit's own date so nobody mistakes it for today's.
 */
export function sheetInspection(b: Bridge): Inspection | null {
  const withElements = b.inspections
    .filter((i) => (i.elements ?? []).length > 0)
    .sort((x, y) => (x.date < y.date ? 1 : -1));
  if (withElements.length) return withElements[0]!;

  /* No breakdown anywhere. The newest inspection still supplies the header —
     who inspected, when, of what type — and the ten rows print empty, which is
     the truthful rendering of a structure inspected without this form. */
  return b.inspections.slice().sort((x, y) => (x.date < y.date ? 1 : -1))[0] ?? null;
}

/**
 * The sheet, in the record's content panel — a docked document like the
 * inventory sheet, whose panel carries Print, PDF and Word (see recordWith in
 * src/screens/detail.ts).
 */
export function inspectionSheetScreen(s: Readonly<State>): HTMLElement {
  const b = findBridge(s.selectedId);
  if (b === null) return emptyState('This register has no structures in it yet.');
  const insp = sheetInspection(b);

  const today = new Date().toISOString().slice(0, 10);
  /* What was open on the day of the inspection this sheet summarises, not
     today (LC-63): a defect repaired since was still work to recommend then,
     and one found since was not yet found. Read off each defect's status
     history; a sheet with no inspection behind it reads as at today. */
  const defects = openOn(s.registerDefects.filter((d) => d.bridgeId === b.id), insp?.date ?? today);
  const est = estimateFor(b, defects, s.rateTables, today, undefined, wordingMap(s.treatmentWording));

  const byElement = new Map<string, ElementCondition>(
    (insp?.elements ?? []).map((e) => [e.element, e]),
  );

  const sheet = h(
    'div',
    { class: 'sheet', id: 'dor-summary-sheet', 'aria-label': `Bridge Inspection Summary — ${b.name}` },
    identification(b),
    summary(b, insp),
    itemTable(byElement, insp),
    recommendations(est, defects),
    signature(),
    /* Where the form came from, on the form. A printed sheet that matches
       DoR's layout but does not say which document defines that layout
       invites the reader to assume this application invented it. */
    h('div', {
      class: 'sheet__prov',
      text:
        'Form as Appendix C, Guidelines for Inspection and Maintenance of Bridges, Volume 1, ' +
        'Part 2 — Bridge Inspection Manual. Condition, extent and urgency scales as §5.3. ' +
        'Produced from the Bridge Watch register; blank boxes are items this register holds ' +
        'no assessment for.',
    }),
  );

  /* On paper (src/components/paper.ts), under the department's head and over
     the foot of every sheet. */
  return paper(
    { title: 'Bridge inspection summary', detail: `${b.id} · ${b.name}`, from: 'Bridge inspection' },
    sheet,
  );
}

/* --------------------------- the blocks --------------------------- */

function identification(b: Bridge): HTMLElement {
  return h(
    'div',
    { class: 'sheet__block' },
    h('div', { class: 'sheet__head', text: 'BRIDGE IDENTIFICATION' }),
    h(
      'div',
      { class: 'sheet__fields' },
      pair('Bridge Number', b.id),
      pair('Name', b.name),
      pair(vocab().area, shown(b.district)),
      pair('Road Link Identification', join([b.route, b.roadName], ' \u2014 ')),
    ),
  );
}

/**
 * The defects open at the end of a day: found by then, and not resolved,
 * repaired or verified as that day ended (LC-63).
 */
export function openOn(defects: readonly RegisterDefect[], date: string): RegisterDefect[] {
  return defects.filter((d) => {
    const status = statusOn(d, date);
    return status !== null && isOpenStatus(status);
  });
}

/**
 * Date, type, inspector, division.
 *
 * "Inspected By" is the inspection's own `inspector` — who recorded it, from
 * the verified session, since LC-08 — and never its reviewer: printing the
 * reviewer there would put one person's name against another person's work on
 * a document that gets signed and filed. An inspection recorded before the
 * register kept its inspector prints the box blank, for the pen, and says so.
 * "Division" is the record's division office, blank where none is recorded.
 */
function summary(b: Bridge, insp: Inspection | null): HTMLElement {
  return h(
    'div',
    { class: 'sheet__block' },
    h('div', { class: 'sheet__head', text: 'INSPECTION SUMMARY' }),
    h(
      'div',
      { class: 'sheet__fields' },
      pair('Date of Inspection', insp ? prettyDate(insp.date) : ''),
      pair('Inspection Type', insp ? inspectionTypeLabel(insp.type) : ''),
      pair('Inspected By', insp?.inspector ?? ''),
      pair('Division', b.divisionOffice ?? ''),
    ),
    insp && !insp.inspector
      ? h('div', {
          class: 'sheet__note',
          text:
            (insp.reviewer ? `Accepted in the register by ${insp.reviewer}. ` : '') +
            'The register does not record who carried out this inspection, so "Inspected By" is left to be completed by hand.',
        })
      : null,
  );
}

function pair(label: string, value: string): HTMLElement {
  return h(
    'div',
    { class: 'sheet__pair' },
    h('span', { class: 'sheet__label', text: label }),
    h('span', { class: 'sheet__value', text: value }),
  );
}

/**
 * The ten items, with DoR's three boxes and a comments column.
 *
 * Every item is a row whether or not it was assessed, because that is what the
 * form is: a checklist of the ten parts of a bridge. Dropping the unassessed
 * ones would turn a sheet showing that two of ten items were looked at into a
 * sheet showing two items.
 */
function itemTable(byElement: Map<string, ElementCondition>, insp: Inspection | null): HTMLElement {
  const max = insp ? insp.scale : '';

  return h(
    'div',
    { class: 'sheet__block' },
    h(
      'div',
      { class: 'sheet__head' },
      h('span', { text: 'INSPECTION SUMMARY' }),
      max ? h('span', { class: 'sheet__scale', text: ` — condition on the ${max} scale` }) : null,
    ),
    h(
      'table',
      { class: 'sheet__tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { class: 'sheet__n', text: 'Item' }),
          h('th', { text: 'Description' }),
          h('th', { class: 'sheet__box', title: 'Condition — §5.3(a)', text: 'C' }),
          h('th', { class: 'sheet__box', title: 'Extent — §5.3(b)', text: 'E' }),
          h('th', { class: 'sheet__box', title: 'Urgency — §5.3(c)', text: 'U' }),
          h('th', { text: 'Comments' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...SUMMARY_ITEMS.map((item) => {
          const e = byElement.get(item.id);
          return h(
            'tr',
            {},
            h('td', { class: 'sheet__n', text: String(item.formItem ?? '') }),
            h('td', { text: item.label }),
            h('td', {
              class: 'sheet__box',
              title: conditionTitle(e),
              text: e === undefined ? '' : e.rating === 'N' ? 'N' : String(e.rating),
            }),
            h('td', {
              class: 'sheet__box',
              title: e?.extent === undefined ? '' : (EXTENT_DEFINITIONS[e.extent] ?? ''),
              text: e?.extent === undefined ? '' : String(e.extent),
            }),
            h('td', {
              class: 'sheet__box',
              title: e?.urgency === undefined ? '' : (URGENCY_DEFINITIONS[e.urgency] ?? ''),
              text: e?.urgency === undefined ? '' : String(e.urgency),
            }),
            h('td', { class: 'sheet__comment', text: e?.note ?? '' }),
          );
        }),
      ),
    ),
  );
}

function conditionTitle(e: ElementCondition | undefined): string {
  if (e === undefined) return 'Not assessed';
  if (e.rating === 'N') return 'Not applicable, or element not accessible for inspection';
  return CONDITION_LABELS[e.rating] ?? '';
}

/**
 * RECOMMENDATIONS FOR REMEDIAL WORK, and an approximate cost.
 *
 * The block the guideline says the summary sheet carries, and the reason this
 * whole item exists. Its shape follows §5.4, which splits the recommendations
 * into minor or routine items — where "a note of the approximate quantities
 * involved will assist later in the preparation of cost estimates" — and serious
 * problems requiring major repair or further investigation.
 *
 * So the rows carry the class, and the ones the register cannot price carry the
 * reason rather than a blank. A blank in a money column on a signed document is
 * read as zero.
 */
function recommendations(est: Estimate, defects: readonly RegisterDefect[]): HTMLElement {
  const byId = new Map(defects.map((d) => [d.id, d]));

  return h(
    'div',
    { class: 'sheet__block' },
    h('div', { class: 'sheet__head', text: 'RECOMMENDATIONS FOR REMEDIAL WORK' }),
    est.lines.length
      ? h(
          'table',
          { class: 'sheet__tbl sheet__tbl--rec' },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { text: 'Location' }),
              h('th', { text: 'Recommended work' }),
              h('th', { class: 'sheet__cls', text: 'Class' }),
              h('th', { class: 'sheet__qty', text: 'Quantity' }),
              h('th', { class: 'sheet__amt', text: 'Approximate cost' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...est.lines.map((l) => {
              const d = byId.get(l.defectId);
              return h(
                'tr',
                {},
                h('td', { text: l.where }),
                h(
                  'td',
                  {},
                  h('div', { text: l.treatment.label }),
                  /* The finding under the recommendation. A sheet listing "resin
                     injection, 12 m" without saying what was seen is a work
                     order rather than an inspection summary. */
                  d
                    ? h('div', {
                        class: 'sheet__finding',
                        text: d.quantity
                          ? `${d.note || l.code} — ${describeQuantity(d.code, d.quantity)}`
                          : d.note || l.code,
                      })
                    : null,
                ),
                h('td', {
                  class: 'sheet__cls',
                  text: isCostable(l.klass) ? l.klass : `${l.klass} *`,
                }),
                h('td', {
                  class: 'sheet__qty',
                  text: l.quantity === null ? '' : `${trimNumber(l.quantity)} ${l.unit}`,
                }),
                h('td', {
                  class: ['sheet__amt', l.amount === null && 'sheet__amt--none'],
                  text: l.amount === null ? refusalText(l.refusal!) : npr(l.amount),
                }),
              );
            }),
            ...est.additions.map((a) =>
              h(
                'tr',
                {},
                h('td', { colspan: '4', text: `${a.label} at ${a.percent}%` }),
                h('td', { class: 'sheet__amt', text: npr(a.amount) }),
              ),
            ),
            est.subtotal > 0
              ? h(
                  'tr',
                  { class: 'sheet__total' },
                  h('td', { colspan: '4', text: 'Approximate cost of the work priced above' }),
                  h('td', { class: 'sheet__amt', text: npr(est.total) }),
                )
              : null,
          ),
        )
      : h('div', {
          class: 'sheet__none',
          text: 'No open issues are recorded against this structure.',
        }),

    /* Two notes, and both are load-bearing on a document that gets signed. */
    est.unpriced['requires-design'] > 0
      ? h('div', {
          class: 'sheet__note',
          text:
            '* Requires detailed investigation, design and cost estimation before it can be ' +
            'estimated — Programme Appraisal Document BIMP Table 3.3. These items are not in the ' +
            'total above and are, in general, the most significant work on this structure.',
        })
      : null,
    h('div', { class: 'sheet__note', text: totalCaveat(est) }),
  );
}

function signature(): HTMLElement {
  return h(
    'div',
    { class: 'sheet__sign' },
    h('span', { text: 'Signed ..............................................' }),
    h('span', { class: 'spacer' }),
    h('span', { text: 'Date ....................................' }),
  );
}
